import React, { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import type { BrowserPlatform } from '../../../../hooks/useBrowserPlatform'
import type { PermissionSubject } from '../../../../services/browserPermissions'
import type { WebFeatureState } from '../../../../services/webPlatformFeatures'
import {
  readSubscriptionState,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  type WebPushSubscriptionState,
} from '../../../../services/webPushClient'
import {
  MobileCapabilityNotice,
  MobileCard,
  MobileChip,
  MobilePrimaryButton,
  MobileSecondaryButton,
  MobileSectionTitle,
  type MobileTone,
} from './WorkerMobilePrimitives'

/**
 * Permissions Center — the browser equivalent of the Android app's permission
 * screens.
 *
 * Two rules govern everything here:
 *   1. Never offer a control that cannot succeed. If the platform requires a
 *      Home Screen install, or the browser has no such capability at all, the
 *      worker is told exactly that instead of being given a dead button.
 *   2. Never nag. A permission the worker explicitly refused is not re-
 *      requested automatically; only an explicit tap tries again.
 */

type PermissionRowState =
  | 'granted'
  | 'not_requested'
  | 'denied'
  | 'unsupported'
  | 'requires_browser_settings'
  | 'requires_install'
  | 'disabled_by_tenant'
  | 'setup_required'
  | 'available'

const toneFor = (state: PermissionRowState): MobileTone => {
  if (state === 'granted' || state === 'available') return 'positive'
  if (state === 'denied' || state === 'requires_browser_settings') return 'critical'
  if (state === 'unsupported' || state === 'disabled_by_tenant' || state === 'setup_required') return 'neutral'
  return 'warning'
}

/**
 * Combine the feature resolution (entitlement + capability) with the live
 * permission grant into one state the worker can act on.
 */
export function resolvePermissionRowState(input: {
  feature: WebFeatureState
  permission?: 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown'
  declined?: boolean
}): PermissionRowState {
  const { feature, permission, declined } = input
  if (feature.availability === 'disabled_by_tenant') return 'disabled_by_tenant'
  if (feature.availability === 'browser_unsupported') return 'unsupported'
  if (feature.availability === 'installation_required') return 'requires_install'
  if (feature.availability === 'setup_required') return 'setup_required'
  if (permission === 'granted') return 'granted'
  // A refusal the browser reports as permanent can only be undone in browser
  // settings — the page can no longer prompt for it.
  if (permission === 'denied') return declined ? 'requires_browser_settings' : 'denied'
  if (permission === undefined) return feature.usable ? 'available' : 'not_requested'
  return 'not_requested'
}

const PermissionRow: React.FC<{
  title: string
  purpose: string
  state: PermissionRowState
  remediation: string
  onGrant?: () => void
  grantLabel: string
  testId: string
}> = ({ title, purpose, state, remediation, onGrant, grantLabel, testId }) => {
  const { t } = useI18n()
  const label: Record<PermissionRowState, string> = {
    granted: t('Granted'),
    available: t('Available'),
    not_requested: t('Not requested'),
    denied: t('Denied'),
    unsupported: t('Unsupported in this browser'),
    requires_browser_settings: t('Requires browser settings'),
    requires_install: t('Requires Home Screen install'),
    disabled_by_tenant: t('Turned off by your organization'),
    setup_required: t('Setup required'),
  }
  const canRequest = onGrant && (state === 'not_requested' || state === 'denied')
  return (
    <MobileCard tone={toneFor(state)} testId={testId}>
      <div className="wm-card__heading">
        <div>
          <h3>{title}</h3>
          <p>{purpose}</p>
        </div>
        <MobileChip tone={toneFor(state)} testId={`${testId}-state`}>{label[state]}</MobileChip>
      </div>
      {state !== 'granted' && state !== 'available' ? <p className="wm-guidance">{remediation}</p> : null}
      {canRequest ? <MobileSecondaryButton label={grantLabel} onClick={onGrant!} testId={`${testId}-grant`} /> : null}
    </MobileCard>
  )
}

export const WorkerMobilePermissions: React.FC<{ platform: BrowserPlatform }> = ({ platform }) => {
  const { t } = useI18n()
  const { capabilities, features, permissions, declined } = platform
  const [pushState, setPushState] = useState<WebPushSubscriptionState>('not_subscribed')
  const [pushBusy, setPushBusy] = useState(false)
  const [pushMessage, setPushMessage] = useState('')

  const refreshPush = useCallback(async () => {
    setPushState(await readSubscriptionState(platform.pushConfig))
  }, [platform.pushConfig])

  useEffect(() => { void refreshPush() }, [refreshPush])

  const grant = (subject: PermissionSubject) => () => { void platform.grant(subject) }

  const enablePush = async () => {
    setPushBusy(true)
    setPushMessage('')
    const result = await subscribeToWebPush(platform.pushConfig)
    setPushState(result.state)
    if (result.state === 'permission_denied') {
      setPushMessage(t('Notifications are blocked for this site. Re-enable them in your browser settings and try again.'))
    } else if (result.message) {
      setPushMessage(result.message)
    }
    await platform.refreshPermissions()
    setPushBusy(false)
  }

  const disablePush = async () => {
    setPushBusy(true)
    await unsubscribeFromWebPush()
    await refreshPush()
    setPushBusy(false)
  }

  return (
    <div className="wm-screen" data-testid="worker-mobile-permissions">
      <MobileSectionTitle
        title={t('App permissions')}
        detail={t('These control what this browser is allowed to do. Your organization still decides what the app itself can do.')}
      />

      {/* Installability. iOS exposes no programmatic install, so it gets the
          real Share → Add to Home Screen steps instead of a button. */}
      <MobileCard testId="worker-mobile-install-card">
        <div className="wm-card__heading">
          <div>
            <h3>{t('Install this app')}</h3>
            <p>{t('Installing gives you a full-screen app, a Home Screen icon, and offline access to your shifts.')}</p>
          </div>
          <MobileChip tone={capabilities.standalone ? 'positive' : 'neutral'}>
            {capabilities.standalone ? t('Installed') : t('Not installed')}
          </MobileChip>
        </div>
        {capabilities.standalone ? null : platform.installPromptReady ? (
          <MobilePrimaryButton label={t('Install app')} onClick={() => void platform.install()} testId="worker-mobile-install" />
        ) : (
          <ol className="wm-steps" data-testid="worker-mobile-install-steps">
            <li>{t('Open the browser share menu.')}</li>
            <li>{t('Choose “Add to Home Screen”.')}</li>
            <li>{t('Open NexusWorkforce360 from your Home Screen.')}</li>
          </ol>
        )}
      </MobileCard>

      <PermissionRow
        testId="worker-mobile-permission-location"
        title={t('Location')}
        purpose={t('Used when you punch in or out, mark yourself on the way, and while you are on an active shift.')}
        state={resolvePermissionRowState({
          feature: features.location,
          permission: permissions.geolocation,
          declined: declined('geolocation'),
        })}
        remediation={features.location.availability === 'disabled_by_tenant'
          ? t('Your organization has not enabled browser location for this app.')
          : features.location.availability === 'browser_unsupported'
            ? t('This browser does not provide location. Open the app in Chrome or Safari on your phone.')
            : declined('geolocation')
              ? t('You previously refused location. Re-enable it for this site in your browser settings, then reload.')
              : t('Allow location when your browser asks. Onsite punches cannot be submitted without it.')}
        onGrant={grant('geolocation')}
        grantLabel={t('Allow location')}
      />

      <PermissionRow
        testId="worker-mobile-permission-notifications"
        title={t('Notifications')}
        purpose={t('Shift reminders, assignment changes, messages, and timesheet updates.')}
        state={resolvePermissionRowState({
          feature: features.push,
          permission: permissions.notifications,
          declined: declined('notifications'),
        })}
        remediation={features.push.availability === 'disabled_by_tenant'
          ? t('Your organization has not enabled browser notifications for this app.')
          : features.push.availability === 'setup_required'
            ? t('Browser notifications are not configured for this deployment yet.')
            : features.push.availability === 'installation_required'
              ? t('On iPhone and iPad, notifications work only after you add this app to your Home Screen.')
              : declined('notifications')
                ? t('You previously refused notifications. Re-enable them for this site in your browser settings.')
                : t('Allow notifications when your browser asks.')}
        onGrant={features.push.availability === 'installation_required' ? undefined : grant('notifications')}
        grantLabel={t('Allow notifications')}
      />

      {/* iOS Web Push requires installation first. Say so explicitly rather
          than presenting an enable flow that cannot complete. */}
      {features.push.availability === 'installation_required' ? (
        <MobileCapabilityNotice
          tone="info"
          testId="worker-mobile-ios-push-notice"
          title={t('To receive shift alerts on this iPhone')}
          detail={t('1. Add NexusWorkforce360 to your Home Screen. 2. Open it from the Home Screen. 3. Enable notifications when prompted.')}
        />
      ) : null}

      {features.push.availability === 'available' || features.push.availability === 'permission_required' ? (
        <MobileCard testId="worker-mobile-push-card">
          <div className="wm-card__heading">
            <div>
              <h3>{t('Push notifications on this device')}</h3>
              <p>{t('Delivers the same notifications you already receive in the app.')}</p>
            </div>
            <MobileChip tone={pushState === 'subscribed' ? 'positive' : 'neutral'}>
              {pushState === 'subscribed' ? t('On') : t('Off')}
            </MobileChip>
          </div>
          {pushMessage ? <p className="wm-guidance" role="status">{pushMessage}</p> : null}
          {pushState === 'subscribed' ? (
            <MobileSecondaryButton label={t('Turn off on this device')} onClick={() => void disablePush()} disabled={pushBusy} testId="worker-mobile-push-off" />
          ) : (
            <MobilePrimaryButton label={t('Turn on notifications')} onClick={() => void enablePush()} busy={pushBusy} testId="worker-mobile-push-on" />
          )}
        </MobileCard>
      ) : null}

      <PermissionRow
        testId="worker-mobile-permission-camera"
        title={t('Camera')}
        purpose={t('Take a profile photo or capture a certificate, licence, or ID.')}
        state={resolvePermissionRowState({
          feature: features.camera,
          permission: permissions.camera,
          declined: declined('camera'),
        })}
        remediation={features.camera.availability === 'disabled_by_tenant'
          ? t('Your organization has not enabled browser document capture.')
          : features.camera.availability === 'browser_unsupported'
            ? t('This browser cannot open the camera. You can still upload a photo from your files.')
            : t('Allow camera access when your browser asks. You can always upload an existing photo instead.')}
        onGrant={grant('camera')}
        grantLabel={t('Allow camera')}
      />

      {features.voice.availability !== 'disabled_by_tenant' ? (
        <PermissionRow
          testId="worker-mobile-permission-microphone"
          title={t('Microphone')}
          purpose={t('Used only where a voice feature is enabled for your organization.')}
          state={resolvePermissionRowState({
            feature: features.voice,
            permission: permissions.microphone,
            declined: declined('microphone'),
          })}
          remediation={t('Allow microphone access when your browser asks.')}
          onGrant={grant('microphone')}
          grantLabel={t('Allow microphone')}
        />
      ) : null}

      <MobileCard tone={features.offline.usable ? 'positive' : 'neutral'} testId="worker-mobile-permission-offline">
        <div className="wm-card__heading">
          <div>
            <h3>{t('Offline storage')}</h3>
            <p>{t('Keeps your shifts readable offline and queues punches until you reconnect.')}</p>
          </div>
          <MobileChip tone={features.offline.usable ? 'positive' : 'neutral'}>
            {features.offline.usable ? t('Available') : features.offline.availability === 'disabled_by_tenant' ? t('Turned off by your organization') : t('Unsupported in this browser')}
          </MobileChip>
        </div>
        <p className="wm-guidance">
          {t('Offline data is a convenience copy, never the official record. Anything waiting to sync stays visible until the server confirms it.')}
        </p>
        {platform.queue.total ? (
          <div className="wm-chip-row">
            <MobileChip tone="warning">{t('{count} pending sync', { count: String(platform.queue.pending) })}</MobileChip>
            <MobileChip tone="critical">{t('{count} failed', { count: String(platform.queue.failed) })}</MobileChip>
            <MobileChip tone="warning">{t('{count} awaiting review', { count: String(platform.queue.requiresReview) })}</MobileChip>
          </div>
        ) : null}
      </MobileCard>

      <MobileCard tone={features.backgroundSync.usable ? 'positive' : 'neutral'} testId="worker-mobile-permission-background">
        <div className="wm-card__heading">
          <div>
            <h3>{t('Background sync')}</h3>
            <p>{t('Lets the app finish sending queued actions after you close it.')}</p>
          </div>
          <MobileChip tone={features.backgroundSync.usable ? 'positive' : 'neutral'}>
            {features.backgroundSync.usable ? t('Available') : t('Unsupported in this browser')}
          </MobileChip>
        </div>
        {!features.backgroundSync.usable ? (
          <p className="wm-guidance">
            {t('This browser has no background sync. Queued actions are sent the next time you open the app while online — nothing is lost.')}
          </p>
        ) : null}
      </MobileCard>

      {/* An honest statement of the platform limit, not a hidden caveat. */}
      {capabilities.backgroundLocationUnavailable ? (
        <MobileCapabilityNotice
          tone="warning"
          testId="worker-mobile-background-location-notice"
          title={t('Background location is not available in a browser')}
          detail={t('While this app is open, location works normally. When your phone closes or suspends it, the browser stops sharing location — so continuous tracking and automatic geofence punches are not available here. Punching in and out still works whenever you open the app.')}
        />
      ) : null}
    </div>
  )
}
